import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Select, Modal, Button, message } from 'antd';
import { getAgents } from 'actions/landlordPropertiesActions';
import { apiPutRequest } from 'helpers/ApiHelper';
import { getTranslatedString } from 'helpers/i18n';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import { ApplicationUIContext, Form } from '@yuhuinc/ui-library';

const { Option } = Select;

const translate = (key) =>
  getTranslatedString(`landlordPortal.applicationManagement.leaseOverview.${key}`);

const ReAssignLease = ({ availableAgents, getAgents }) => {
  const currentApplication = useContext(CurrentApplicationContext);
  const { isMobile } = useContext(ApplicationUIContext);
  const queryClient = useQueryClient();
  const [assignedAgentForm] = Form.useForm();
  const selectedAgentId = Form.useWatch('assigned_agent', assignedAgentForm);
  const reassignLeaseAgentMutation = useMutation({
    mutationFn: (data) =>
      apiPutRequest(
        `/landlords/application_management/applications/${currentApplication.id}/assign_agent`,
        {
          agent_id: data.assigned_agent,
        }
      ),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      message.success(translate('overview.assignedAgent.success'));
    },
  });

  useEffect(() => {
    getAgents({ accessible_property_id: currentApplication?.property?.id });
  }, [currentApplication?.property?.id]);

  const onSubmit = (data) => {
    const buttonProps = { size: isMobile ? 'large' : 'default' };
    const bodyStyle = { fontSize: isMobile ? 16 : 14 };

    Modal.confirm({
      title: translate('overview.assignedAgent.confirmTitle'),
      content: translate('overview.assignedAgent.confirmMessage'),
      onOk: () => reassignLeaseAgentMutation.mutate(data),
      onCancel: () => assignedAgentForm.resetFields(),
      okText: getTranslatedString('yes'),
      cancelText: getTranslatedString('no'),
      okButtonProps: buttonProps,
      cancelButtonProps: buttonProps,
      bodyStyle,
    });
  };

  return (
    <Form
      layout="vertical"
      form={assignedAgentForm}
      initialValues={{ assigned_agent: currentApplication?.assigned_agent?.id }}
      onFinish={onSubmit}
    >
      <Form.Item label={translate('overview.assignedAgent')} name="assigned_agent">
        <Select
          showSearch
          placeholder={translate('overview.assignedAgent.selectAnAgent')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          style={{ maxWidth: 456 }}
          size={isMobile && 'large'}
        >
          {availableAgents.map((agent) => (
            <Option key={agent.value} value={agent.value}>
              {agent.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Button
        htmlType="submit"
        disabled={
          selectedAgentId === currentApplication?.assigned_agent?.id ||
          reassignLeaseAgentMutation.isLoading
        }
        loading={reassignLeaseAgentMutation.isLoading}
      >
        {translate('overview.assignedAgent.submit')}
      </Button>
    </Form>
  );
};

const mapStoreToProps = (store) => ({
  availableAgents: store.landlordProperties.agents,
});

const mapDispatchToProps = {
  getAgents,
};

export default connect(mapStoreToProps, mapDispatchToProps)(ReAssignLease);
